import { useState } from 'react';
import { motion } from 'framer-motion';
import { Scale, Briefcase, CheckCircle, ArrowRight, Check, Medal, Target, Shield, Lightbulb, Users, BarChart2, Link as LinkIcon, Phone, BookOpen, PieChart, ChevronRight, TrendingUp, Clock, Star, Award } from "lucide-react";
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import ContactForm from '@/components/ContactForm';
import CallToAction from '@/components/CallToAction';

const SpecializedIndustries = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        seo={{
          title: "Specialized Industry SEO Services | Los Angeles SEO Group",
          description: "Expert SEO services for legal, insurance, financial services, healthcare, and other specialized industries with compliance-focused strategies.",
          canonical: "https://losangelesseogroup.com/services/specialized-industries",
        }}
        url="https://losangelesseogroup.com/services/specialized-industries"
      />
      <Navbar />
      
      <PageTransition>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-canyon-50/50 to-white dark:from-canyon-950/20 dark:to-background">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div 
                  initial="hidden" 
                  animate="visible" 
                  variants={fadeIn}
                >
                  <div className="space-y-6">
                    <span className="inline-flex items-center rounded-full bg-canyon-100 dark:bg-canyon-900/30 px-3 py-1 text-sm font-medium text-canyon-800 dark:text-canyon-300">
                      <Scale className="w-4 h-4 mr-1" />
                      Specialized Industry SEO
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading">
                      Expert SEO for 
                      <span className="text-canyon-600 dark:text-canyon-400 block mt-2">
                        Highly Regulated Industries
                      </span>
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-xl">
                      Compliance-focused SEO strategies for legal, insurance, financial services, 
                      healthcare, and other specialized industries with unique requirements.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="lg" className="bg-canyon-gradient">
                            Schedule a Strategy Call
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <div className="space-y-4">
                            <h3 className="text-xl font-bold">Schedule Your Strategy Call</h3>
                            <p className="text-sm text-muted-foreground">
                              Fill out the form below and we'll schedule a call with an industry specialist.
                            </p>
                            <ContactForm onSuccess={() => setDialogOpen(false)} />
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-canyon-100 dark:bg-canyon-900/30 text-canyon-600 dark:text-canyon-400 font-medium transition-colors hover:bg-canyon-200 dark:hover:bg-canyon-900/50"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-3">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center border-2 border-background">
                            <Award className="w-4 h-4 text-canyon-400" />
                          </div>
                        ))}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Industry-specific SEO experts</span>
                        <span className="block text-muted-foreground">with compliance expertise</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="lg:px-6"
                >
                  <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
                    <div className="p-6 space-y-5">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-canyon-100 dark:bg-canyon-900/50 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-canyon-600 dark:text-canyon-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Industry-Specific SEO Expertise</h3>
                            <p className="text-xs text-muted-foreground">Specialized solutions for complex industries</p>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                          Compliant
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {[
                          { industry: "Legal SEO", details: "Personal injury, family law, criminal defense" },
                          { industry: "Financial Services", details: "Wealth management, loans, insurance" },
                          { industry: "Healthcare", details: "Medical practices, clinics, specialists" },
                          { industry: "Real Estate", details: "Brokerages, property management, agents" }
                        ].map((item, i) => (
                          <div key={i} className="flex justify-between items-center border-b border-border pb-3 last:border-0 last:pb-0">
                            <span className="text-sm font-medium">{item.industry}</span>
                            <div className="text-right">
                              <span className="block text-xs text-muted-foreground">{item.details}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 border-t border-border">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Find your industry's custom solution</span>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="link" className="text-canyon-600 dark:text-canyon-400 gap-1 p-0">
                              Talk to an expert <ChevronRight className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <div className="space-y-4">
                              <h3 className="text-xl font-bold">Request a Specialized Industry Consultation</h3>
                              <p className="text-sm text-muted-foreground">
                                Speak with an SEO specialist who understands your industry's unique challenges.
                              </p>
                              <ContactForm />
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Industries Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-white to-canyon-50/30 dark:from-background dark:to-canyon-950/10">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl font-bold font-heading">
                  Specialized SEO for <span className="text-canyon-600 dark:text-canyon-400">Complex Industries</span>
                </h2>
                <p className="mt-4 text-muted-foreground text-lg">
                  Industry-specific strategies that address unique challenges and compliance requirements
                </p>
              </div>
              
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {[
                  {
                    icon: <Scale className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "Legal SEO",
                    description: "Specialized strategies for law firms, tailored to specific practice areas including personal injury, family law, and corporate law."
                  },
                  {
                    icon: <Shield className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "Insurance Industry",
                    description: "Compliance-aware SEO for insurance agencies and brokers, focusing on high-intent keywords that drive qualified leads."
                  },
                  {
                    icon: <BookOpen className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "Healthcare Marketing",
                    description: "HIPAA-compliant SEO strategies for medical practices, specialists, and healthcare organizations of all sizes."
                  },
                  {
                    icon: <TrendingUp className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "Financial Services",
                    description: "Regulatory-compliant SEO for wealth management, banking, and financial advisory firms with ROI-focused campaigns."
                  },
                  {
                    icon: <Briefcase className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "B2B & Professional Services",
                    description: "Strategic SEO for consulting, accounting, IT services, and other B2B companies with long sales cycles."
                  },
                  {
                    icon: <Target className="w-6 h-6 text-canyon-600 dark:text-canyon-400" />,
                    title: "Education & Programs",
                    description: "Enrollment-focused SEO for educational institutions, certification programs, and continuing education providers."
                  }
                ].map((industry, i) => (
                  <motion.div 
                    key={i}
                    variants={fadeIn}
                    className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-lg bg-canyon-100 dark:bg-canyon-900/30 flex items-center justify-center mb-5">
                      {industry.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{industry.title}</h3>
                    <p className="text-muted-foreground">{industry.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
          
          {/* Approach Section */}
          <section className="py-16 md:py-24">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <span className="inline-flex items-center rounded-full bg-canyon-100 dark:bg-canyon-900/30 px-3 py-1 text-sm font-medium text-canyon-800 dark:text-canyon-300 mb-6">
                    <Lightbulb className="w-4 h-4 mr-1" />
                    Our Approach
                  </span>
                  
                  <h2 className="text-3xl font-bold font-heading mb-6">
                    Industry-Specific <span className="text-canyon-600 dark:text-canyon-400">SEO Methodology</span>
                  </h2>
                  
                  <p className="text-muted-foreground mb-8">
                    Our specialized approach addresses the unique challenges, compliance requirements, and competitive dynamics of your industry.
                  </p>
                  
                  <Tabs defaultValue="research" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="research">Research</TabsTrigger>
                      <TabsTrigger value="strategy">Strategy</TabsTrigger>
                      <TabsTrigger value="execution">Execution</TabsTrigger>
                    </TabsList>
                    <TabsContent value="research" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Industry-Specific Keyword Analysis</p>
                            <p>Comprehensive research to identify high-value industry terminology and search patterns</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Competitive Intelligence</p>
                            <p>Detailed analysis of industry leaders to identify opportunities and benchmark performance</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Compliance & Regulatory Review</p>
                            <p>Assessment of industry-specific regulations that impact marketing and content strategies</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Client Journey Mapping</p>
                            <p>Analysis of the unique decision-making process in your industry to align content strategy</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="strategy" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Compliance-Focused Content Planning</p>
                            <p>Development of content strategies that balance marketing goals with industry regulations</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Authority Building Blueprint</p>
                            <p>Strategic plan to establish your brand as a trusted authority within your specialized field</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Technical SEO Roadmap</p>
                            <p>Prioritized plan to address technical barriers and enhance site performance for your industry</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Conversion Rate Optimization</p>
                            <p>Industry-specific CRO strategies to improve lead quality and conversion rates</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="execution" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Expert Content Development</p>
                            <p>Creation of authoritative content that demonstrates expertise in your field</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Industry-Specific Link Building</p>
                            <p>Acquisition of high-quality backlinks from relevant industry publications and resources</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Technical Implementation</p>
                            <p>Execution of technical optimizations with special attention to industry-specific requirements</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">ROI Tracking & Reporting</p>
                            <p>Comprehensive performance monitoring with metrics that matter to your specific industry</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="mt-8">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="lg" className="bg-canyon-gradient">
                          Schedule a Strategy Call
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold">Book Your Industry-Specific Strategy Call</h3>
                          <p className="text-sm text-muted-foreground">
                            Speak with a specialist who understands your industry's unique requirements.
                          </p>
                          <ContactForm />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-canyon-100 to-amber-100 dark:from-canyon-900/20 dark:to-amber-900/20 p-1 rounded-2xl shadow-xl">
                    <div className="bg-card rounded-xl p-8 border border-border">
                      <div className="space-y-6">
                        <div className="flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-canyon-100 dark:bg-canyon-900/30 flex items-center justify-center">
                            <Medal className="w-5 h-5 text-canyon-600 dark:text-canyon-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">Specialized Industries Package</h3>
                            <p className="text-sm text-muted-foreground">Expert SEO for regulated markets</p>
                          </div>
                        </div>
                        
                        <div className="pt-2">
                          <div className="text-3xl font-bold">$5,000<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                          <p className="text-sm text-muted-foreground mt-1">Custom pricing based on industry and competition</p>
                        </div>
                        
                        <div className="space-y-3 pt-3">
                          {[
                            "Industry-specific keyword optimization",
                            "Compliance-aware content strategy",
                            "Authority building campaigns",
                            "Expert content development",
                            "Competitive analysis",
                            "ROI tracking",
                            "Dedicated account manager"
                          ].map((feature, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4 space-y-3">
                          <Button className="w-full bg-canyon-gradient" size="lg" asChild>
                            <Link to="/contact">Get Started</Link>
                          </Button>
                          
                          <div className="text-center">
                            <a 
                              href="tel:+13237841577" 
                              className="text-sm text-canyon-600 dark:text-canyon-400 font-medium hover:underline flex items-center justify-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              Call for custom pricing: (323) 784-1577
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -z-10 -bottom-6 -right-6 w-64 h-64 bg-canyon-200/50 dark:bg-canyon-900/10 rounded-full blur-3xl"></div>
                  <div className="absolute -z-10 -top-6 -left-6 w-48 h-48 bg-amber-200/50 dark:bg-amber-900/10 rounded-full blur-3xl"></div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Case Studies Section */}
          <section className="py-16 md:py-24 bg-muted/50">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Success Stories from <span className="text-canyon-600 dark:text-canyon-400">Specialized Industries</span>
                </h2>
                <p className="mt-4 text-muted-foreground">
                  See how we've helped clients in regulated industries achieve significant growth
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: "Personal Injury Law Firm",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Case Inquiries", value: "+186%" },
                      { label: "Qualified Leads", value: "+119%" },
                      { label: "ROI", value: "4.7x" }
                    ],
                    summary: "Comprehensive SEO strategy with practice area-specific content and authority building.",
                    link: "/case-studies"
                  },
                  {
                    title: "Financial Advisory Firm",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Organic Traffic", value: "+152%" },
                      { label: "Consultation Requests", value: "+87%" },
                      { label: "Client Acquisition Cost", value: "-35%" }
                    ],
                    summary: "Compliance-focused strategy with authoritative content and local visibility campaign.",
                    link: "/case-studies"
                  },
                  {
                    title: "Medical Practice Group",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "New Patient Inquiries", value: "+94%" },
                      { label: "Procedure-Specific Traffic", value: "+167%" },
                      { label: "Appointment Bookings", value: "+112%" }
                    ],
                    summary: "HIPAA-compliant SEO approach with specialty-focused content strategy.",
                    link: "/case-studies"
                  }
                ].map((study, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-video bg-muted relative">
                      <img 
                        src={study.image} 
                        alt={study.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                        <h3 className="text-white font-bold text-xl p-4">{study.title}</h3>
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {study.stats.map((stat, j) => (
                          <div key={j} className="text-center">
                            <div className="text-lg font-bold text-canyon-600 dark:text-canyon-400">{stat.value}</div>
                            <div className="text-xs text-muted-foreground">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        {study.summary}
                      </p>
                      
                      <Link 
                        to={study.link}
                        className="flex items-center text-sm font-medium text-canyon-600 dark:text-canyon-400 hover:text-canyon-700 dark:hover:text-canyon-300"
                      >
                        Read Case Study <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button asChild variant="outline" className="gap-1">
                  <Link to="/case-studies">
                    View All Case Studies <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
          
          {/* FAQ Section */}
          <section className="py-16 md:py-24">
            <div className="page-container max-w-4xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Frequently Asked Questions About <span className="text-canyon-600 dark:text-canyon-400">Specialized Industry SEO</span>
                </h2>
              </div>
              
              <div className="space-y-6">
                {[
                  {
                    q: "How do you handle industry compliance requirements in SEO?",
                    a: "We maintain a dedicated compliance team that stays current with regulations in legal, financial, healthcare, and other regulated industries. Our content development process includes compliance review to ensure all materials meet industry standards while still being effective for SEO purposes."
                  },
                  {
                    q: "What makes specialized industry SEO different from general SEO services?",
                    a: "Specialized industry SEO incorporates deep knowledge of industry-specific terminology, compliance requirements, and competitive dynamics. We understand the unique buyer journey in complex industries with longer sales cycles and emphasize E-A-T (Expertise, Authority, Trustworthiness) principles that are crucial in these fields."
                  },
                  {
                    q: "How long does it take to see results in specialized industries?",
                    a: "Highly competitive industries like legal, financial services, and healthcare typically have longer timeframes for SEO success, often 6-12 months for significant results. However, we implement a staged approach that can show early improvements in areas like local visibility and technical optimization within the first 3-4 months."
                  },
                  {
                    q: "Do you have experience with my specific industry vertical?",
                    a: "We have specialists with experience across numerous specialized industries including personal injury law, family law, wealth management, insurance, medical specialties, and more. During our initial consultation, we'll match you with a specialist familiar with your specific vertical and its unique challenges."
                  },
                  {
                    q: "How do you measure ROI for specialized industry SEO?",
                    a: "We develop custom ROI tracking methodologies for each industry that account for longer sales cycles and higher client values. This includes tracking leads through your CRM, implementing call tracking, monitoring form submissions, and calculating client acquisition costs against lifetime client value."
                  }
                ].map((faq, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true, margin: "-50px" }}
                    className="bg-card border border-border rounded-lg p-6 shadow-sm"
                  >
                    <h3 className="text-lg font-semibold mb-3">{faq.q}</h3>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10 text-center">
                <p className="text-muted-foreground mb-4">Have questions specific to your industry?</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <a 
                    href="tel:+13237841577" 
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-canyon-100 dark:bg-canyon-900/30 text-canyon-600 dark:text-canyon-400 font-medium transition-colors hover:bg-canyon-200 dark:hover:bg-canyon-900/50"
                  >
                    <Phone className="w-5 h-5" />
                    <span>(323) 784-1577</span>
                  </a>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>Contact Us Online</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <div className="space-y-4">
                        <h3 className="text-xl font-bold">Industry-Specific Questions?</h3>
                        <p className="text-sm text-muted-foreground">
                          Fill out the form below and an industry specialist will contact you.
                        </p>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="py-16 md:py-20 bg-gradient-to-br from-canyon-50 to-amber-50 dark:from-canyon-950/30 dark:to-amber-950/30">
            <div className="page-container">
              <div className="max-w-4xl mx-auto bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
                <div className="p-8 md:p-10">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold font-heading">
                      Ready for SEO That Understands Your <span className="text-canyon-600 dark:text-canyon-400">Industry's Unique Needs</span>?
                    </h2>
                    <p className="mt-3 text-muted-foreground">
                      Schedule a consultation with a specialist who understands the complexities of your field
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                    <div className="md:col-span-2">
                      <ContactForm />
                    </div>
                    
                    <div className="bg-muted/60 p-6 rounded-xl border border-border/50">
                      <h3 className="font-semibold mb-3">Or Call Us Directly</h3>
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center gap-2 px-5 py-3 rounded-lg bg-canyon-100 dark:bg-canyon-900/30 text-canyon-600 dark:text-canyon-400 font-medium transition-colors hover:bg-canyon-200 dark:hover:bg-canyon-900/50 w-full justify-center"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        Speak directly with an industry SEO specialist about your goals
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <CallToAction />
        </main>
      </PageTransition>
      
      <Footer />
    </div>
  );
};

export default SpecializedIndustries;
